<template>
  <div v-if="!!params" class="vpagination">
    <div class="section-params">
      <div v-if="totalPages > 1" class="pagination">
        <div class="pages">
          <vbutton
            v-if="currentPage !== 1"
            class="left"
            :label="$tr('common.list.previous')"
            :disabled="isLoading"
            color="white"
            @click="prevPage"
          />

          <div
            v-for="(page, i) in pages"
            :key="i"
            :class="{ 'active-class': page.selected, 'disabled-class': page.disabled }"
          >
            <a v-if="page.breakView" tabindex="0" :disabled="isLoading">...</a>
            <a v-else-if="page.disabled" tabindex="0" :disabled="isLoading">
              {{ page.content }}
            </a>
            <a
              v-else
              class="asd"
              tabindex="0"
              :disabled="isLoading"
              @click="changePage(page.index + 1)"
            >
              {{ page.content }}
            </a>
          </div>

          <vbutton
            v-if="currentPage !== totalPages"
            class="right"
            color="white"
            :label="$tr('common.list.next')"
            :disabled="isLoading"
            @click="nextPage"
          />
        </div>
      </div>

      <div v-if="!isLastPage && showLoadmoreButton" class="section-more">
        <vbutton
          class="width-full"
          color="white"
          :label="$tr('admin_console.companies.table.showMore')"
          :disabled="isLoading"
          @click="$emit('on-load-more')"
        />
      </div>

      <div
        v-if="showCounts && totalPages > 1"
        class="count-block"
        :class="{
          'count-block--right': totalPages < 2 && (isLastPage || !showLoadmoreButton)
        }"
      >
        <multiselect
          v-if="!isLoading"
          ref="multiselect"
          v-model="paginationLimit"
          class="counts-select"
          :class="{ big: isBigSelect }"
          :options="counts"
          :disabled="isLoading"
          :show-labels="false"
          :is-left="isLeft"
          :searchable="false"
          track-by="val"
          label="title"
          allow-empty
          :placeholder="$tr('admin_console.companies.showBy')"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex';

  export default {
    name: 'VcvPagination',

    props: {
      params: {
        type: Object,
        default: () => {}
      },

      showLoadmoreButton: {
        type: Boolean,
        default: false
      },

      showCounts: {
        type: Boolean,
        default: false
      },

      isLoading: {
        type: Boolean,
        default: false
      },

      isLeft: {
        type: Boolean,
        default: false
      },

      isBigSelect: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        counts: [
          {
            title: this.$tr('admin_console.companies.showByCount', { count: 5 }),
            val: 5
          },
          {
            title: this.$tr('admin_console.companies.showByCount', { count: 10 }),
            val: 10
          },
          {
            title: this.$tr('admin_console.companies.showByCount', { count: 20 }),
            val: 20
          },
          {
            title: this.$tr('admin_console.companies.showByCount', { count: 30 }),
            val: 30
          },
          {
            title: this.$tr('admin_console.companies.showByCount', { count: 50 }),
            val: 50
          }
        ]
      };
    },

    computed: {
      ...mapGetters('locale', { locale: 'current' }),
      // locale() {
      //   return this.$store.getters['locale/current'];
      // },
      totalPages() {
        return this.params ? this.params._page_count : 1;
      },

      currentPage() {
        if (!this.params) {
          return 1;
        }

        return this.params._page;
      },

      isLastPage() {
        if (!this.params) {
          return true;
        }

        return (
          this.currentPage * this.paginationLimit.val >= this.totalPages * this.paginationLimit.val
        );
      },

      list() {
        const arr = [];
        const page = this.currentPage;
        const totalPages = this.totalPages;

        let leftBoundry = Math.max(1, page - 1);
        let rightBoundry = Math.min(totalPages, page + 1);

        const emptyRight = 1 - (rightBoundry - page);
        const emptyLeft = 1 - (page - leftBoundry);

        leftBoundry = Math.max(1, leftBoundry - emptyRight);
        rightBoundry = Math.min(totalPages, rightBoundry + emptyLeft);

        for (let i = leftBoundry; i <= rightBoundry; i++) {
          arr.push(i);
        }

        return arr;
      },

      pages() {
        let items = {};
        const pageCount = this.totalPages;
        const pageRange = 3;
        const selected = this.currentPage;
        const marginPages = 1;

        if (pageCount <= pageRange) {
          for (let index = 0; index < pageCount; index++) {
            let page = {
              index: index,
              content: index + 1,
              selected: index === selected - 1
            };
            items[index] = page;
          }
        } else {
          const halfPageRange = Math.floor(pageRange / 2);
          let setPageItem = index => {
            let page = {
              index: index,
              content: index + 1,
              selected: index === selected - 1
            };
            items[index] = page;
          };
          let setBreakView = index => {
            let breakView = {
              disabled: true,
              breakView: true
            };
            items[index] = breakView;
          };
          // 1st - loop thru low end of margin pages
          for (let i = 0; i < marginPages; i++) {
            setPageItem(i);
          }
          // 2nd - loop thru selected range
          let selectedRangeLow = 0;
          if (selected - halfPageRange > 0) {
            selectedRangeLow = selected - 1 - halfPageRange;
          }
          let selectedRangeHigh = selectedRangeLow + pageRange - 1;
          if (selectedRangeHigh >= pageCount) {
            selectedRangeHigh = pageCount - 1;
            selectedRangeLow = selectedRangeHigh - pageRange + 1;
          }
          for (let i = selectedRangeLow; i <= selectedRangeHigh && i <= pageCount - 1; i++) {
            setPageItem(i);
          }
          // Check if there is breakView in the left of selected range
          if (selectedRangeLow > marginPages) {
            setBreakView(selectedRangeLow - 1);
          }
          // Check if there is breakView in the right of selected range
          if (selectedRangeHigh + 1 < pageCount - marginPages) {
            setBreakView(selectedRangeHigh + 1);
          }
          // 3rd - loop thru high end of margin pages
          for (let i = pageCount - 1; i >= pageCount - marginPages; i--) {
            setPageItem(i);
          }
        }
        return items;
      },

      paginationLimit: {
        get() {
          if (!this.params) {
            return this.counts[2];
          }

          return {
            title:
              this.locale === 'ja'
                ? `${this.params._limit} ${this.$tr('admin_console.companies.showBy')}`
                : `${this.$tr('admin_console.companies.showBy')} ${this.params._limit}`,

            val: this.params._limit
          };
        },

        set(count) {
          if (count) {
            this.$emit('on-limit-change', count.val);
          }
        }
      }
    },

    methods: {
      nextPage() {
        if (this.isLoading) {
          return;
        }

        if (this.currentPage === this.totalPages) {
          return;
        }
        this.changePage(this.currentPage + 1);
      },

      prevPage() {
        if (this.isLoading) {
          return;
        }

        if (this.currentPage === 1) {
          return;
        }
        this.changePage(this.currentPage - 1);
      },

      changePage(page) {
        if (this.isLoading) {
          return;
        }

        this.$emit('on-page-change', page);
      }
    }
  };
</script>

<style lang="scss" scoped>
  .pages {
    a {
      display: block;
      width: 100%;
      height: 100%;

      &:hover,
      &:focus,
      &:active {
        outline: none;
        color: inherit;
      }
    }
  }

  .section-params {
    margin-top: 30px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 2px;
  }

  .pagination {
    padding: 0;
    width: auto;

    .pages {
      div {
        background: #fff;
        border-radius: 0;
        border: 1px solid #d3d6da;
        min-width: 40px;
        line-height: 38px;
        font-size: 16px;
        margin-right: 0;
        max-height: 40px;
        min-height: 40px;

        &:hover {
          background: #f4f5f7;
        }
      }

      .left {
        margin-right: 15px;
        border-radius: 2px;
      }

      .right {
        margin-left: 15px;
        border-radius: 2px;
      }

      .first {
        border-top-left-radius: 2px;
        border-bottom-left-radius: 2px;
      }

      .last {
        border-top-right-radius: 2px;
        border-bottom-right-radius: 2px;
      }

      .active {
        color: #333;
        background: #f4f5f7;
      }

      .active-class {
        color: #333;
        background: #f4f5f7;
        pointer-events: none;
      }

      .disabled-class {
        color: #333;
        background: #f4f5f7;
        pointer-events: none;
        opacity: 0.65;
      }
    }
  }

  .section-more {
    display: flex;
    justify-content: center;
    width: 150px;
  }

  .vpagination {
    margin-top: 30px;
    margin-bottom: 30px;
  }

  .multiselect .multiselect__tags {
    min-width: calc(100% - 7px);
  }

  .count-block--right {
    margin-left: auto;
  }
</style>
